import {CommentsCategory} from '@hconnect/common/types'
import {MenuItem} from '@material-ui/core'
import {Field, FieldInputProps, FormikProps} from 'formik'
import React from 'react'

import {useAdminPilotUser} from '../../../../common/hooks/useAdminPilotUser'
import {useCustomerByNameFeature} from '../../../../hooks/useFeatures'
import {useTranslation} from '../../../../hooks/useTranslation'
import {LookupDataScopeInput} from '../../../../modules/DataScopeConfiguration'
import {
  ADD_CUSTOMER,
  ADD_CUSTOMER_SCOPE,
  ADD_HAULIER,
  ADD_PAYER,
  ADD_PLANT,
  GroupedRoleReducerAction
} from '../../../../modules/GroupedRoleAssignmentReducer'
import {Permission} from '../../../../modules/LoggedInUser'
import {
  DataScopes,
  GroupedRoleAssignment,
  isRoleTypeUngroupable
} from '../../../../modules/ManageUsers.selectors'
import {RoleCommentCategoryLookupSelect} from '../../../deprecated/RoleCommentCategoryLookupSelect'
import {RoleCustomerLookupTextField} from '../../../deprecated/RoleCustomerLookupTextField'
import {RoleHaulierLookupTextField} from '../../../deprecated/RoleHaulierLookupTextField'
import {RolePayerLookupTextField} from '../../../deprecated/RolePayerLookupTextField'
import {RolePlantLookupTextField} from '../../../deprecated/RolePlantLookupTextField'
import {RoleContractLookupTextField} from '../../../RoleContractLookupTextField'
import {RoleCustomerLookupDropdownTextField} from '../../../RoleCustomerLookupDropdownTextField'
import {checkRequired} from '../../utils'

interface Props {
  isRequired: boolean
  errorMessage: string
  configuration: LookupDataScopeInput
  groupedRoleAssignment: GroupedRoleAssignment
  dataScopeName: string
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
  multiSelect: boolean
  setShowCustomerOrPayerDropdown: (show: boolean) => void
  showCustomerOrPayerDropdown: boolean
  customerAdminData?: Record<string, string | undefined>
  loggedInUserPermissions?: Permission[] | null
  roleDataScopeNames?: Set<string>
}

const CommentsCategoryTranslationKeys: Record<CommentsCategory, string> = {
  [CommentsCategory.Assets]: 'assets',
  [CommentsCategory.AssetOperationTimes]: 'assetoperationtimes',
  [CommentsCategory.POMQuestions]: 'POMquestions',
  [CommentsCategory.MaterialOrders]: 'materialorders',
  [CommentsCategory.HrocRegionalManagerKpis]: 'hrocRegionalManagerKpis',
  [CommentsCategory.ProcessWorkOrders]: 'processWorkOrders'
}

export const LookupDataScope: React.FC<Props> = ({
  isRequired,
  errorMessage,
  configuration,
  groupedRoleAssignment,
  dataScopeName,
  dispatchRoleStateUpdate,
  multiSelect,
  customerAdminData,
  setShowCustomerOrPayerDropdown,
  showCustomerOrPayerDropdown,
  loggedInUserPermissions,
  roleDataScopeNames
}) => {
  const {t} = useTranslation()
  const fieldName = `commonDataScopes.${dataScopeName}`
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)

  const {isCustomersByNameSearch, isLoading} = useCustomerByNameFeature()

  return (
    <Field
      key={dataScopeName}
      name={fieldName}
      validate={(value: string) => {
        if (groupedRoleAssignment.variantDataScopes.customersScopes.length > 0) {
          // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for customers.
          return
        }
        if (
          dataScopeName === 'payerIds' &&
          groupedRoleAssignment.variantDataScopes.payerIds &&
          groupedRoleAssignment.variantDataScopes.payerIds.length > 0
        ) {
          // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for payers.
          return
        }
        if (
          dataScopeName === 'plantIds' &&
          groupedRoleAssignment.variantDataScopes.plantIds &&
          groupedRoleAssignment.variantDataScopes.plantIds.length > 0
        ) {
          // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for plants.
          return
        }
        if (
          dataScopeName === 'haulierIds' &&
          groupedRoleAssignment.variantDataScopes.haulierIds &&
          groupedRoleAssignment.variantDataScopes.haulierIds.length > 0
        ) {
          // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for hauliers.
          return
        }

        return checkRequired(
          isRequired,
          multiSelect,
          t('roleAssignment.dataScopeRequired', {
            dataScope: t(`roleAssignment.dataScope.${dataScopeName}`)
          })
        )(value)
      }}
    >
      {
        // eslint-disable-next-line complexity
        ({
          field,
          form
        }: {
          field: FieldInputProps<string[]>
          form: FormikProps<{commonDataScopes: DataScopes}>
        }) => {
          const dependenciesMet =
            configuration.dependsOn !== undefined
              ? !configuration.dependsOn
                  .filter((dep) => !roleDataScopeNames || roleDataScopeNames.has(dep))
                  .map((dep) => form.values.commonDataScopes[dep])
                  .some((dependency) => dependency === undefined)
              : true

          const customerAdminPayerDependenciesMet =
            isUserAdminPilot && groupedRoleAssignment.variantDataScopes.customersScopes.length === 0
          // FIXME Add properly working customers and payers list
          // const customersList =
          //   field?.value?.map?.((customerId: string) => getCustomerById(customers, customerId)) || []
          const customersList = []

          // const payersList =
          //   field?.value?.map?.((payerId: string) => getPayerById(payers, payerId)) || []

          const payersList = []
          if (
            dataScopeName === 'payerIds' &&
            (isUserAdminPilot
              ? showCustomerOrPayerDropdown ||
                groupedRoleAssignment.variantDataScopes.payerIds?.length === undefined ||
                groupedRoleAssignment.variantDataScopes.payerIds?.length === 0
              : true)
          ) {
            return (
              <RolePayerLookupTextField
                InputLabelProps={{required: isRequired}}
                payers={payersList}
                groupedRoleAssignment={groupedRoleAssignment}
                onChangePayerIds={(payerIds: string[]) => {
                  dispatchRoleStateUpdate({
                    type: ADD_PAYER,
                    payload: payerIds[0]
                  })
                }}
                disabled={isUserAdminPilot ? !customerAdminPayerDependenciesMet : !dependenciesMet}
                name={field.name}
                margin="normal"
                fullWidth
                helperText={errorMessage}
                dataScopeName={dataScopeName}
                customerAdminData={customerAdminData}
                setShowCustomerOrPayerDropdown={setShowCustomerOrPayerDropdown}
                loggedInUserPermissions={loggedInUserPermissions}
              />
            )
          }

          if (dataScopeName === 'plantIds' || dataScopeName === 'plantId') {
            return (
              <RolePlantLookupTextField
                InputLabelProps={{required: isRequired}}
                groupedRoleAssignment={groupedRoleAssignment}
                dataScopeName={dataScopeName}
                disabled={!dependenciesMet}
                helperText={errorMessage}
                onChangePlantIds={(plantId: string) => {
                  dispatchRoleStateUpdate({
                    type: ADD_PLANT,
                    payload: plantId
                  })
                }}
              />
            )
          }

          if (dataScopeName === 'commentCategories') {
            return (
              <RoleCommentCategoryLookupSelect
                groupedRoleAssignment={groupedRoleAssignment}
                dataScopeName={dataScopeName}
                disabled={!dependenciesMet}
                onChange={(commentCategory: string) => {
                  dispatchRoleStateUpdate({
                    type: 'ADD_COMMENT_CATEGORY',
                    payload: commentCategory
                  })
                }}
              >
                {/* This is an annoying manual implementation but it's better than typing it into a free text field*/}
                {Object.values(CommentsCategory).map((category) => (
                  <MenuItem key={category} value={category}>
                    {t(
                      `roleAssignment.commentCategoryItems.${CommentsCategoryTranslationKeys[category]}`
                    )}
                  </MenuItem>
                ))}
              </RoleCommentCategoryLookupSelect>
            )
          }
          if (dataScopeName === 'contractIds') {
            return (
              <RoleContractLookupTextField
                InputLabelProps={{required: isRequired}}
                groupedRoleAssignment={groupedRoleAssignment}
                dataScopeName={dataScopeName}
                disabled={!dependenciesMet}
                helperText={errorMessage}
                onChangeContractIds={(contractId) => {
                  dispatchRoleStateUpdate({
                    type: ADD_CUSTOMER_SCOPE,
                    payload: {
                      contractId
                    }
                  })
                }}
              />
            )
          }

          const hauliersList = []

          if (dataScopeName === 'haulierIds') {
            return (
              <RoleHaulierLookupTextField
                InputLabelProps={{
                  required: isRequired
                }}
                hauliers={hauliersList}
                groupedRoleAssignment={groupedRoleAssignment}
                onChangeHaulierIds={(haulierIds) => {
                  dispatchRoleStateUpdate({
                    type: ADD_HAULIER,
                    payload: haulierIds[0]
                  })
                }}
                disabled={!dependenciesMet}
                name={field.name}
                margin="normal"
                fullWidth
                helperText={errorMessage}
                dataScopeName={dataScopeName}
              />
            )
          }

          const isCustomerSelected =
            groupedRoleAssignment.variantDataScopes.customersScopes.length > 0

          const hide = isRoleTypeUngroupable(groupedRoleAssignment.roleType) && isCustomerSelected

          if (hide) {
            return null
          }
          if (isLoading) {
            return null
          } else if (isCustomersByNameSearch) {
            return (
              <RoleCustomerLookupDropdownTextField
                InputLabelProps={{
                  required: isRequired
                }}
                customers={customersList}
                groupedRoleAssignment={groupedRoleAssignment}
                onChangeCustomerIds={(customerIds) => {
                  dispatchRoleStateUpdate({
                    type: ADD_CUSTOMER,
                    payload: customerIds[0]
                  })
                }}
                disabled={!dependenciesMet}
                name={field.name}
                margin="normal"
                fullWidth
                helperText={errorMessage}
                dataScopeName={dataScopeName}
              />
            )
          } else {
            return !isUserAdminPilot ||
              showCustomerOrPayerDropdown ||
              (dataScopeName !== 'payerIds' &&
                groupedRoleAssignment.variantDataScopes.customersScopes.length === 0) ? (
              <RoleCustomerLookupTextField
                InputLabelProps={{
                  required: isRequired
                }}
                customers={customersList}
                groupedRoleAssignment={groupedRoleAssignment}
                onChangeCustomerIds={(customerIds) => {
                  dispatchRoleStateUpdate({
                    type: ADD_CUSTOMER,
                    payload: customerIds[0]
                  })
                }}
                disabled={isUserAdminPilot ? !isUserAdminPilot : !dependenciesMet}
                name={field.name}
                margin="normal"
                fullWidth
                helperText={errorMessage}
                dataScopeName={dataScopeName}
                customerAdminData={customerAdminData}
                showCustomerOrPayerDropdown={showCustomerOrPayerDropdown}
                setShowCustomerOrPayerDropdown={setShowCustomerOrPayerDropdown}
                loggedInUserPermissions={loggedInUserPermissions}
              />
            ) : null
          }
        }
      }
    </Field>
  )
}
